import { useCallback } from "react";

// Manual authenticated download of a generated document.
//
// Generated documents are stored privately, so `document-url` from the API is
// an authenticated proxy endpoint rather than a public storage link. A plain
// anchor navigation would drop the bearer token and 401, so fetch the bytes
// with the token and hand the browser a blob. Mirrors useDownloadAttachment.
//
// Takes the absolute URL the API returned so this works unchanged for both a
// single project document and the multi-document zip bundle.
const filenameFromDisposition = (
  header: string | null,
  fallback: string
): string => {
  const match = header?.match(/filename="?([^"]+)"?/);
  return match?.[1] ?? fallback;
};

export const useDownloadGeneratedDocument = (accessToken: string) =>
  useCallback(
    async (
      documentUrl: string,
      fallbackFilename = "download"
    ): Promise<void> => {
      const response = await fetch(documentUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(
        response.headers.get("Content-Disposition"),
        fallbackFilename
      );
      // Append to the document so the browser reliably honors the click before
      // we revoke the object URL (a synchronous revoke after click() races the
      // browser's async fetch of the blob and can yield a 0-byte file).
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 10_000);
    },
    [accessToken]
  );

export default useDownloadGeneratedDocument;
